import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid2,
  Link,
  Stack,
  Typography,
  useTheme,
  LinearProgress,
} from "@mui/material";

import {
  ChipParticipant,
  DetailsTodo,
  InvestigationCollapsable,
  Separator,
} from "../components/InvestigationComponents";
import { InvestigationButton } from "../components/InvestigationButton";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { AddResourceForActivity } from "./Modals/AddResourceForActivity";
import { AddTodoForActivity } from "./Modals/AddTodoForActivity";
import { activitySchema } from "./schemas";
import { enqueueSnackbar } from "notistack";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { SectionedSelect } from "../../../../MainComponents/Select/SectionedSelect";
import { GenerateWithIA } from "../components/InvestigationComponents";

import { generateAiInvestigtionPlan } from "../../../../../apis/apiAi";
import { catalogByPart } from "../../../../../apis";
import { useParams } from "react-router";

const validateForm = (form) => {
  const errors = {};

  if (!form.name) {
    errors.name = true;
  }

  return [Object.entries(errors).length === 0 ? true : false, errors];
};

const AIProgressMessages = ({ isGenerating, onComplete }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const messages = [
    "Ordenando información...",
    "Informando al investigador virtual...",
    "Analizando datos del reporte...",
    "Generando estructura del plan...",
    "Creando archivo de investigación...",
    "Optimizando recomendaciones...",
    "Validando coherencia del contenido...",
    "Aplicando mejores prácticas...",
    "Personalizando actividades...",
    "Finalizando plan de actividades...",
    "Preparando resultado final...",
  ];

  useEffect(() => {
    if (!isGenerating) {
      setCurrentMessageIndex(0);
      setProgress(0);
      return;
    }

    // Cambiar mensaje cada 8 segundos
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        if (prev < messages.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 8000);

    // Actualizar progreso cada 500ms
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95; // No llegar al 100% hasta que termine
        return prev + Math.random() * 3 + 1;
      });
    }, 500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [isGenerating]);

  // Completar al 100% cuando termine
  useEffect(() => {
    if (!isGenerating && progress > 0) {
      setProgress(100);
      setTimeout(() => {
        onComplete && onComplete();
      }, 500);
    }
  }, [isGenerating, progress, onComplete]);

  if (!isGenerating) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
        borderRadius: 2,
        padding: 3,
        marginBottom: 2,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#1976d2",
          marginBottom: 2,
          fontWeight: 500,
        }}
      >
        🤖 Generando plan de actividades con IA
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "#666",
          marginBottom: 3,
          minHeight: 24,
          fontWeight: 500,
        }}
      >
        {messages[currentMessageIndex]}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e3f2fd",
          "& .MuiLinearProgress-bar": {
            backgroundColor: "#1976d2",
            borderRadius: 4,
          },
        }}
      />

      <Typography
        variant="caption"
        sx={{
          color: "#888",
          marginTop: 1,
          display: "block",
        }}
      >
        {Math.round(progress)}% completado
      </Typography>
    </Box>
  );
};

export const AddAndEditPlan = ({
  plans,
  setPlans,
  selectedActivity,
  setSelectedActivity,
  setIsEditing,
  handleSavePlans,
  investigation,
  setInvestigation,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const { id } = useParams();

  const {
    enums,
    functions: { getResourceLabel },
    plans: { statusOpts },
    selects: { hoursOpts, typeUserLabels },
  } = useConstants();
  const jaguarState = useSelector((state) => state.jaguar);
  const { email } = useSelector((state) => state.auth);
  const { accessResourcesInfo, financialResourcesInfo, materialResourcesInfo } =
    jaguarState.currentReport.investigation;

  const [activity, setActivity] = useState({..._.cloneDeep(activitySchema), assignedTo: { id: email, from: enums.typeUser.ME }});
  const [party, setParty] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [todos, setTodos] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);

  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  const [newParticipant, setNewParticipant] = useState(null);

  const [activeDetails, setActiveDetails] = useState(false);
  const [activeDeadline, setActiveDeadline] = useState(false);
  const [showAddResource, setShowAddResource] = useState(false);
  const [showAddTodo, setShowAddTodo] = useState(false);

  // For IA Generator
  const [iaGenerator, setIaGenerator] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // For activityPlan catalogue
  const [activityPlanOptions, setActivityPlanOptions] = useState([]);
  const [isCustomActivityName, setIsCustomActivityName] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);

    try {
      const plan = await generateAiInvestigtionPlan({
        report_id: id,
        investigation_alias: investigation.alias,
      });
      setPlans(plan);
      setIsEditing(false);
    } catch (error) {
      console.error("Error generando contenido:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateAIContent = () => {
    // Simular llamada a API que toma entre 30s y 2 minutos
    return new Promise((resolve) => {
      const randomTime = Math.random() * (120000 - 30000) + 30000; // 30s a 2min
      setTimeout(resolve, randomTime);
    });
  };

  // Select for assign the activity
  const assignedToSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: t("activityPlan.assign_to_me"),
            },
          ],
        });
        return;
      }
      const fromArray =
        _.get(jaguarState, enums.typeUserRoutes[value])?.formatted || [];
      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [party, jaguarState]);

  // Select for add participants
  const participantsSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: t("activityPlan.participate_in_the_activity"),
            },
          ],
        });
        return;
      }
      const fromArray =
        _.get(jaguarState, enums.typeUserRoutes[value])?.formatted || [];
      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Kick assigned from selections
    const { id, from } = activity?.assignedTo ?? {};
    const findedSectionIdx = newSections.findIndex(
      (section) => section.key === from
    );
    if (findedSectionIdx !== -1) {
      newSections[findedSectionIdx].options = newSections[
        findedSectionIdx
      ].options.map((option) => {
        if (option.value === id) {
          return { ...option, disabled: true };
        }
        return option;
      });
    }

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [activity.assignedTo, party, jaguarState]);

  const existsResources = useMemo(() => {
    return (
      accessResourcesInfo.approved.length > 0 ||
      financialResourcesInfo.approved.length > 0 ||
      materialResourcesInfo.approved.length > 0
    );
  }, [accessResourcesInfo, financialResourcesInfo, materialResourcesInfo]);

  useEffect(() => {
    const loadActivityPlanOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "activityPlan",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label || item.value,
            value: item.path_locales || item.value,
          }));
          // Agregar opción "Definir Otro" al final
          options.push({
            label: t("activityPlan.define_other"),
            value: "__other__",
          });
          setActivityPlanOptions(options);
        } else {
          setActivityPlanOptions([{
            label: t("activityPlan.define_other"),
            value: "__other__",
          }]);
        }
      } catch (error) {
        console.error('Error loading activityPlan catalogue:', error);
        setActivityPlanOptions([{
          label: t("activityPlan.define_other"),
          value: "__other__",
        }]);
      }
    };

    loadActivityPlanOptions();
  }, []);

  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(activity);
      setErrors(errors);
    }
  }, [activity.name]);

  useEffect(() => {
    return () => {
      resetValues();
    };
  }, []);

  useEffect(() => {
    if (selectedActivity != null) {
      setActivity(plans[selectedActivity]);
      setParty(plans[selectedActivity].party || []);
      setResources(plans[selectedActivity].resources || []);
      setTodos(plans[selectedActivity].todos || []);
      setActiveDetails(plans[selectedActivity].details ? true : false);
      setActiveDeadline(plans[selectedActivity].deadline ? true : false);

      // Verificar si el nombre de la actividad es personalizado o del catálogo
      const activityName = plans[selectedActivity].name;
      const isInCatalogue = activityPlanOptions.some(opt => opt.value === activityName);
      setIsCustomActivityName(!isInCatalogue && activityName !== "");
    }
  }, [selectedActivity, activityPlanOptions]);

  // Main ------------------------------------------------------------------------------------------------

  const handleSave = async () => {
    const [isValid, errors] = validateForm(activity);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    if (selectedActivity == null) {
      const newPlans = _.cloneDeep(plans);
      newPlans.push({
        ...activity,
        party,
        resources,
        todos,
        created_at: dayjs().toISOString(),
      });
      await handleSavePlans(newPlans);
    } else {
      const newPlans = _.cloneDeep(plans);
      newPlans[selectedActivity] = {
        ...activity,
        party,
        resources,
        todos,
      };
      await handleSavePlans(newPlans);
    }
  };

  const handleClose = () => {
    setSelectedActivity(null);
    setIsEditing(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "status" && e.target.value !== "completed" && activity.status === "completed" && dayjs(activity.deadline).isBefore(dayjs())) {
      setActivity({ ...activity, [e.target.name]: e.target.value, deadline: "", timeDeadline: "" });
      return;
    }

    // Si seleccionan "Definir Otro" en el nombre de actividad
    if (e.target.name === "name" && e.target.value === "__other__") {
      setIsCustomActivityName(true);
      setActivity({ ...activity, name: "" });
      return;
    }

    setActivity({ ...activity, [e.target.name]: e.target.value });
  };

  const handleChangeResponsible = ({ value, section }) => {
    setNewParticipant(null);
    setActivity({ ...activity, assignedTo: { id: value, from: section } });
  };

  const resetValues = () => {
    setActivity(_.cloneDeep(activitySchema));
    setErrors({});
    setTriedToSave(false);
    setActiveDetails(false);
    setActiveDeadline(false);
    setIsCustomActivityName(false);
  };

  // Party ------------------------------------------------------------------------------------------------

  const handleAddParty = () => {
    setParty([...party, newParticipant]);
    setNewParticipant(null);
  };

  const handleDeleteParty = (idx) => {
    const newParty = _.cloneDeep(party);
    newParty.splice(idx, 1);
    setParty(newParty);
  };

  // Resources ------------------------------------------------------------------------------------------------

  const handleEditResource = (idx) => {
    setSelectedResource(idx);
    setShowAddResource(true);
  };

  const handleDeleteResource = (idx) => {
    const newResources = _.cloneDeep(resources);
    newResources.splice(idx, 1);
    setResources(newResources);
  };

  // Tasks ------------------------------------------------------------------------------------------------

  const handleEditTodo = (idx) => {
    setSelectedTodo(idx);
    setShowAddTodo(true);
  };

  const handleDeleteTodo = (idx) => {
    const newTodos = _.cloneDeep(todos);
    newTodos.splice(idx, 1);
    setTodos(newTodos);
  };

  return (
    <>
      <AddResourceForActivity
        show={showAddResource}
        onHide={() => setShowAddResource(false)}
        resources={resources}
        setResources={setResources}
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
        investigation={investigation}
        setInvestigation={setInvestigation}
      />
      <AddTodoForActivity
        show={showAddTodo}
        onHide={() => setShowAddTodo(false)}
        todos={todos}
        setTodos={setTodos}
        selectedTodo={selectedTodo}
        setSelectedTodo={setSelectedTodo}
      />

      <Box padding={2}>
        <div
          style={{ width: "100%", display: "inline-block", padding: "1em 0em" }}
        >
          <GenerateWithIA
            text={t("activityPlan.generate_with_ai")}
            buttonText={t("activityPlan.generate_with_ai_button")}
            onClick={() => setIaGenerator(true)}
            style={{ float: "right", marginTop: "-1em", marginRight: "0px" }}
          />

          <Separator
            text={t("activityPlan.add_new_activity_plan")}
            sx={{ marginBottom: 2, width: "auto" }}
          />
        </div>

        {!iaGenerator ? (
          <>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <SectionedSelect
                  label={`${t("activityPlan.responsible_of_the_activity")}:`}
                  name="assignedTo"
                  value={activity.assignedTo?.id ?? ""}
                  onChange={handleChangeResponsible}
                  sections={assignedToSections}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                {!isCustomActivityName ? (
                  <EGBasicSelect
                    label={`${t("activityPlan.name_of_the_activity")}:`}
                    name="name"
                    value={activity.name}
                    onChange={handleChange}
                    options={activityPlanOptions}
                    required={true}
                    isValid={returnIsValid(errors?.name, triedToSave) ?? null}
                    showSuccessValidation
                    showHelpText={false}
                  />
                ) : (
                  <>
                    <EGInput
                      label={`${t("activityPlan.name_of_the_activity")}:`}
                      name="name"
                      value={activity.name}
                      onChange={handleChange}
                      required={true}
                      isValid={returnIsValid(errors?.name, triedToSave) ?? null}
                      showSuccessValidation
                      showHelpText={false}
                    />
                    <Box display="flex" justifyContent="flex-end" mt={0.5}>
                      <Link
                        onClick={() => {
                          setIsCustomActivityName(false);
                          setActivity({ ...activity, name: "" });
                        }}
                        underline="hover"
                      >
                        ← {t("activityPlan.back_to_catalogue")}
                      </Link>
                    </Box>
                  </>
                )}
                {!activeDetails && (
                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <Link
                      onClick={() => setActiveDetails(true)}
                      underline="hover"
                    >
                      + {t("activityPlan.add_details_to_the_activity")}
                    </Link>
                  </Box>
                )}
              </Grid2>

              {activeDetails && (
                <Grid2 size={12}  >
                  <EGInput
                    label={`${t("activityPlan.details_of_the_activity")}`}
                    name="details"
                    value={activity.details}
                    onChange={handleChange}
                    type="textarea"
                    multiline
                    rows={3}
                  />
                </Grid2>
              )}

              <Grid2 size={{ xs: 12, md: 6 }} >
                <EGBasicSelect
                  label={t("activityPlan.status")}
                  name="status"
                  value={activity.status}
                  onChange={handleChange}
                  options={statusOpts}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, md: 6 }}
                container
                spacing={2}
              >
                <Grid2 size={9}>
                  <SectionedSelect
                    label={t("activityPlan.participants")}
                    name="party"
                    value={newParticipant?.id ?? ""}
                    onChange={({ value, section }) =>
                      setNewParticipant({ id: value, from: section })
                    }
                    sections={participantsSections}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <InvestigationButton
                    label={`${t("activityPlan.add_alone")}`}
                    icon={false}
                    size="medium"
                    onClick={handleAddParty}
                    sx={{ width: "100%" }}
                    disabled={!newParticipant}
                  />
                </Grid2>
              </Grid2>

              {party.length > 0 && (
                <Grid2 size={12}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {party.map((participant, idx) => (
                      <ChipParticipant
                        key={idx}
                        idx={idx}
                        role={
                          typeof participant === "string"
                            ? t("activityPlan.participant")
                            : typeUserLabels[participant.from]
                        }
                        name={
                          typeof participant === "string"
                            ? participant
                            : _.get(
                                jaguarState,
                                enums.typeUserRoutes[participant.from]
                              )?.formatted.find(
                                (user) => user.value === participant.id
                              )?.label || t("activityPlan.user_not_found")
                        }
                        handleDeleteParticipant={() => handleDeleteParty(idx)}
                      />
                    ))}
                  </Stack>
                </Grid2>
              )}

              <Grid2
                size={{ xs: 12, md: 6 }}
                spacing={2}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height={"100%"}
                >
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    color={theme.palette.secondary.main}
                  >
                    {t("activityPlan.set_deadline")}
                  </Typography>
                  <EGSwitch
                    checked={activeDeadline}
                    onChange={() => setActiveDeadline(!activeDeadline)}
                    size="small"
                    sx={{ marginRight: 0.5 }}
                  />
                </Box>
              </Grid2>

              <Grid2
                size={{ xs: 12, md: 6 }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                {activeDeadline && (
                  <>
                    <EGDatePicker
                      label={`${t("activityPlan.deadline")}`}
                      name="deadline"
                      value={activity.deadline ? dayjs(activity.deadline) : null}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "deadline", value: e.toISOString() },
                        })
                      }
                      className="date-picker-deadline"
                      format="DD/MMM/YYYY"
                      minDate={activity.status === "completed" ? null : dayjs()}
                    />

                    <Box width={140}>
                      <EGBasicSelect
                        label={t("activityPlan.time")}
                        name="timeDeadline"
                        value={activity.timeDeadline}
                        onChange={handleChange}
                        options={hoursOpts}
                      />
                    </Box>
                  </>
                )}
              </Grid2>

            </Grid2>
            <Stack
              spacing={2}
              p={2}
              mt={2}
              borderRadius="5px"
              border={`1px solid ${
                returnIsValid(errors?.interview) == null
                  ? theme.palette.primary.main
                  : returnIsValid(errors?.interview)
                  ? theme.palette.green.main
                  : theme.palette.red.main
              }`}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <InvestigationItemContainer
                  color={theme.palette.primary.main}
                  width="fit-content"
                  sx={{ padding: 0.8 }}
                >
                  <Typography fontSize={13} fontWeight={600}>
                    {t("activityPlan.actions_of_the_activity")}
                  </Typography>
                </InvestigationItemContainer>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="flex-end"
                  spacing={1}
                >
                  <InvestigationButton
                    label={`${t("activityPlan.assign_resource")}`}
                    icon="outline_data_setting"
                    size="medium"
                    onClick={() => setShowAddResource(true)}
                    disabled={!existsResources}
                  />
                  <InvestigationButton
                    label={`${t("activityPlan.add_task")}`}
                    size="medium"
                    icon="add_circle"
                    onClick={() => setShowAddTodo(true)}
                  />
                </Stack>
              </Stack>

              {resources.length > 0 || todos.length > 0 ? (
                <hr className="hr-div" style={{ margin: "0", opacity: 1 }} />
              ) : (
                <Box
                  border={`2px solid ${theme.palette.pink.main}`}
                  borderRadius={"10px"}
                  px={2}
                  py={1.5}
                >
                  <Typography fontSize={16} color="oxfordBlue.main">
                    {t(
                      "activityPlan.assign_resources_to_the_activity"
                    )}
                  </Typography>
                </Box>
              )}

              {
                // Recursos
                resources.length > 0 && (
                  <>
                    <Separator text={t("activityPlan.assigned_resources")} />
                    {resources.map((resource, idx) => (
                      <InvestigationCollapsable
                        title={
                          <Typography
                            color="secondary.main"
                            fontSize={14}
                            fontWeight={600}
                          >
                            {resource.type === "access"
                              ? t("resources.access_resource")
                              : resource.type === "financial"
                              ? t("resources.financial_resource")
                              : resource.type === "material"
                              ? t("resources.material_resource")
                              : t("")}
                            :&nbsp;
                            <Typography
                              component="span"
                              fontSize={16}
                              fontWeight={700}
                            >
                              {getResourceLabel(resource)}
                            </Typography>
                          </Typography>
                        }
                        details={
                          resource.details && (
                            <Typography
                              color="blueGray600.main"
                              fontSize={11}
                              fontWeight={600}
                            >
                              {t("activityPlan.answer")}:&nbsp;
                              <Typography
                                color="oxfordBlue.main"
                                component="span"
                                fontSize={11}
                                fontStyle="italic"
                              >
                                {resource.details}
                              </Typography>
                            </Typography>
                          )
                        }
                        buttons={[
                          {
                            tooltip: t("activityPlan.delete"),
                            name: "trash_can",
                            size: 20,
                            color: theme.palette.red.main,
                            onClick: () => handleDeleteResource(idx),
                          },
                          {
                            tooltip: t("activityPlan.edit"),
                            name: "outline_edit",
                            size: 20,
                            color: theme.palette.primary.main,
                            onClick: () => handleEditResource(idx),
                          },
                        ]}
                      />
                    ))}
                  </>
                )
              }
              {
                // Tareas
                todos.length > 0 && (
                  <>
                    <Separator text={t("activityPlan.assigned_tasks")} />
                    {todos.map((todo, idx) => (
                      <InvestigationCollapsable
                        key={idx}
                        title={
                          <Typography color="secondary.main" fontSize={14}>
                            {t("activityPlan.task")}{" "}
                            {todo.completed ? t("activityPlan.completed") : t("activityPlan.pending").toLocaleLowerCase()}
                            :&nbsp;
                            <Typography
                              component="span"
                              fontSize={16}
                              fontWeight={700}
                            >
                              {typeof todo === "string" ? todo : todo.name}
                            </Typography>
                          </Typography>
                        }
                        details={
                          (todo.assignedTo?.id ||
                            todo.details ||
                            todo.deadline ||
                            todo.dedicatedHours ||
                            todo.party?.length > 0) && (
                            <DetailsTodo todo={todo} />
                          )
                        }
                        buttons={[
                          {
                            tooltip: t("activityPlan.delete"),
                            name: "trash_can",
                            size: 20,
                            color: theme.palette.red.main,
                            onClick: () => handleDeleteTodo(idx),
                          },
                          {
                            tooltip: t("activityPlan.edit"),
                            name: "outline_edit",
                            size: 20,
                            color: theme.palette.primary.main,
                            onClick: () => handleEditTodo(idx),
                          },
                        ]}
                      />
                    ))}
                  </>
                )
              }
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={1}
              mt={2}
            >
              <InvestigationButton
                label={`${t("causes.cancel")}`}
                icon="close_icon"
                size="medium"
                onClick={handleClose}
              />
              <InvestigationButton
                label={`${t("activityPlan.save")}`}
                size="medium"
                variant="success"
                icon="mark_check_bold"
                onClick={handleSave}
              />
            </Stack>
          </>
        ) : (
          <>
            <Box
              sx={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: 1,
                padding: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#856404" }}>
                <strong>{t("activityPlan.important")}:</strong> {t("activityPlan.important_description")}{" "}
                <strong>{t("activityPlan.revise_and_validate")}:</strong> {t("activityPlan.revise_and_validate_description")}.  
              </Typography>
            </Box>

            <AIProgressMessages
              isGenerating={isGenerating}
              onComplete={() => {
                // Aquí puedes agregar lógica adicional cuando termine
                console.log("Generación completada");
              }}
            />

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={1}
              mt={2}
            >
              <InvestigationButton
                label={`${t("causes.cancel")}`}
                icon="close_icon"
                size="medium"
                onClick={() => setIaGenerator(false)}
              />
              <InvestigationButton
                label={`${t("causes.gen")}`}
                variant="success"
                size="medium"
                icon="outline_ai"
                onClick={handleGenerate}
                disabled={isGenerating}
              />
            </Stack>
          </>
        )}

        {/* { // Recursos
          resources.length > 0 &&
          <>
            <Separator text={t("Recursos asignado")} />
            {
              resources.map((resource, idx) => (
                <InvestigationCollapsable
                  title={
                    <Typography color="secondary.main" fontSize={14} fontWeight={600}>
                      {resource.type === enums.typeResource.ACCESS
                        ? t("Recurso de acceso")
                        : resource.type === enums.typeResource.FINANCIAL
                          ? t("Recurso financiero")
                          : resource.type === enums.typeResource.MATERIAL
                            ? t("Recurso material")
                            : t("")}
                      :&nbsp;
                      <Typography component="span" fontSize={16} fontWeight={700}>
                        {(() => {
                          if (resource.type === enums.typeResource.ACCESS) {
                            const resourceLabel = accessResourcesInfo.approvedFormatted.find((opt) => opt.value === resource.resource)?.label;
                            return accessResourcesOpts.find((opt) => opt.value === resourceLabel)?.label || resourceLabel;
                          } else {
                            return _.get(jaguarState, enums.typeResourceRoutes[resource.type])?.formatted.find((opt) => opt.value === resource.resource)?.label || resource.resource;
                          }
                        })()}
                      </Typography>
                    </Typography>
                  }
                  details={resource.details &&
                    <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
                      {t("Respuesta")}:&nbsp;
                      <Typography color="oxfordBlue.main" component="span" fontSize={11} fontStyle="italic">
                        {resource.details}
                      </Typography>
                    </Typography>
                  }
                  buttons={[
                    {
                      tooltip: t("Eliminar"),
                      name: "trash_can",
                      size: 20,
                      color: theme.palette.red.main,
                      onClick: () => handleDeleteResource(idx)
                    },
                    {
                      tooltip: t("Editar"),
                      name: "outline_edit",
                      size: 20,
                      color: theme.palette.primary.main,
                      onClick: () => handleEditResource(idx)
                    },
                  ]}
                />
              ))
            }
          </>
        }
        { // Tareas
          todos.length > 0 &&
          <>
            <Separator text={t("Tareas asignadas")} />
            {
              todos.map((todo, idx) => (
                <InvestigationCollapsable
                  key={idx}
                  title={
                    <Typography color="secondary.main" fontSize={14}>
                      {t("Tarea")} {todo.completed ? t("completada") : t("pendiente")}:&nbsp;
                      <Typography component="span" fontSize={16} fontWeight={700}>
                        {typeof todo === "string" ? todo : todo.name}
                      </Typography>
                    </Typography>
                  }
                  details={(todo.assignedTo?.id || todo.details || todo.deadline || todo.dedicatedHours || todo.party?.length > 0) && <DetailsTodo todo={todo} />}
                  buttons={[
                    {
                      tooltip: t("Eliminar"),
                      name: "trash_can",
                      size: 20,
                      color: theme.palette.red.main,
                      onClick: () => handleDeleteTodo(idx)
                    },
                    {
                      tooltip: t("Editar"),
                      name: "outline_edit",
                      size: 20,
                      color: theme.palette.primary.main,
                      onClick: () => handleEditTodo(idx)
                    },
                  ]}
                />
              ))
            }
          </>
        } */}
      </Box>
    </>
  );
};
